import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/App";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, checkAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from hash
        const hash = location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          navigate("/login");
          return;
        }

        const sessionId = sessionIdMatch[1];

        // Use XMLHttpRequest to avoid "object cannot be cloned" DOMException
        const data = await new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open("POST", `${API}/auth/session`, true);
          xhr.setRequestHeader("Content-Type", "application/json");
          xhr.withCredentials = true;
          
          xhr.onload = function() {
            if (xhr.status >= 200 && xhr.status < 300) {
              try {
                resolve(JSON.parse(xhr.responseText));
              } catch (e) {
                reject(new Error("Parse error"));
              }
            } else {
              // Parse error details for better UX
              try {
                const errData = JSON.parse(xhr.responseText);
                const detail = errData.detail;
                if (detail?.error === "invalid_request" || detail?.error_description?.includes("state")) {
                  reject(new Error("AUTH_STATE_EXPIRED"));
                } else {
                  reject(new Error(detail?.error_description || detail?.error || "Auth failed"));
                }
              } catch {
                reject(new Error("Auth failed"));
              }
            }
          };
          
          xhr.onerror = function() {
            reject(new Error("Network error"));
          };
          
          xhr.send(JSON.stringify({ session_id: sessionId }));
        });

        login(data.user);
        
        // Navigate to dashboard with user data
        navigate("/", { state: { user: data.user } });
      } catch (error) {
        console.error("Auth callback error:", error);
        if (error.message === "AUTH_STATE_EXPIRED") {
          // State expired — usually means the session timed out or cookies were blocked
          // Retry by re-initiating the auth flow
          const redirectUrl = window.location.origin + '/dashboard';
          // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
          window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
        } else {
          navigate("/login", { state: { error: "Sign in failed. Please try again." } });
        }
      }
    };

    processAuth();
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center">
      <div className="text-center">
        <div className="animate-spin w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full mx-auto mb-4"></div>
        <p className="text-white">Completing sign in...</p>
      </div>
    </div>
  );
}
